import { useProfile } from "@/context/useProfileProps";
import { getEducationPrefillData } from "@/helpers/getProfilePrefillData";
import { handleFocusOnField } from "@/helpers/helper";
import { ModalLoadingTypes } from "@/types/common";
import { SalaryNudge } from "@monsterindia/l1-apop";
import { useCallback, useEffect, useMemo, useState } from "react";

import ProfileModal from "@/components/common/profileModal";
import { default as EducationEditCard } from "@/components/profile/educationCard/edit";
import { useInvalidateProfileQueries } from "@/components/profile/query";

import { default as WorkEditCard } from "../workExperience/edit";
import BasicFields from "./components/BasicFields";
import Widget from "./components/widget";
import { EditProps } from "./types";

const EditCard = ({
  isEdit,
  closeEdit,
  prefilledData,
  educationData,
  employmentData,
  isCalledFromOutside = false,
  handleFooterBtnLoading, // to handle loading for profile score or called from outside
  formRef,
  formLevel,
  handleFormLevel,
  focusOnField = "",
  workAuthChangeRequired = false,
}: EditProps) => {
  const [loading, setLoading] = useState({
    isSaveLoading: false,
    isDeleteLoading: false,
  });
  const {
    isMobile,
    translation: { basic },
  } = useProfile();
  const { invalidateProfileQueries } = useInvalidateProfileQueries();
  const [salaryNudgePrefillData, setSalaryNudgePrefillData] = useState<
    any | null
  >(null);

  useEffect(() => {
    handleFocusOnField(focusOnField);
  }, [focusOnField]);

  const isExperienced = prefilledData?.experienceLevel === "EXPERIENCED";

  const handleSave = useCallback(() => formRef.current?.requestSubmit(), []);

  const pageHeading = useMemo(() => {
    switch (formLevel) {
      case 1:
        return basic.edTitle;
      case 2:
        return basic.empTitle;
      default:
        return basic.basicTitle;
    }
  }, [formLevel]);

  const handleLoading = (e: ModalLoadingTypes) => {
    setLoading((prev) => ({ ...prev, ...e }));

    // to handle loading state of save button for profile score or called from outside
    if (e.isSaveLoading !== undefined && handleFooterBtnLoading) {
      handleFooterBtnLoading({ isSaveLoading: e.isSaveLoading });
    }
  };

  const handleWorkEditClose = (prevAction = "", data = null) => {
    if (prevAction === "SALARY_NUDGE" && data) {
      setSalaryNudgePrefillData(data);
    } else {
      handleFormLevel(0);
    }
  };
  const renderForm = (
    <>
      {formLevel === 0 && (
        <div>
          <BasicFields
            personalData={prefilledData}
            handleLoading={handleLoading}
            closeEdit={closeEdit}
            formRef={formRef}
            workAuthChangeRequired={workAuthChangeRequired}
          />
          <Widget
            educationData={!isExperienced ? educationData : {}}
            employmentData={isExperienced ? employmentData : {}}
            setForm={handleFormLevel}
          />
        </div>
      )}
      {formLevel === 1 && (
        <EducationEditCard
          isEdit={true}
          isCalledFromOutside={true}
          prefilledData={getEducationPrefillData(educationData)}
          handleFooterBtnLoading={handleLoading}
          closeEdit={() => handleFormLevel(0)}
          formRef={formRef}
        />
      )}
      {formLevel === 2 && (
        <WorkEditCard
          isEdit={true}
          isCalledFromOutside={true}
          prefilledData={{ employmentPrefill: employmentData }}
          handleFooterBtnLoading={handleLoading}
          closeEdit={handleWorkEditClose}
          formRef={formRef}
        />
      )}
    </>
  );

  return salaryNudgePrefillData ? (
    <SalaryNudge
      prefilledData={salaryNudgePrefillData}
      isL1Apop={false}
      screenAlignment={window.innerWidth < 768 ? "bottom-sheet" : "center"}
      closeApop={() => {
        setSalaryNudgePrefillData(null);
        invalidateProfileQueries();
        handleFormLevel(0);
      }}
    />
  ) : !isCalledFromOutside ? (
    <ProfileModal
      id="details-modal"
      showModal={isEdit}
      closeModal={closeEdit}
      isMobile={isMobile}
      showCloseButton={true}
      screenAlignment={isMobile ? "bottom-sheet" : "center"}
      showHandleBar={isMobile}
      heading={pageHeading}
      customClass={isMobile ? "" : "h-[70vh]"}
      footerPrimaryBtnLabel="Save"
      footerPrimaryBtnHandler={handleSave}
      footerPrimaryBtnIsLoading={loading.isSaveLoading}
      footerSecondaryBtnIsLoading={loading.isDeleteLoading}
      backBtnhandler={formLevel !== 0 ? () => handleFormLevel(0) : undefined}
    >
      {renderForm}
    </ProfileModal>
  ) : (
    renderForm
  );
};

export default EditCard;
